export const MEASUREMENT_UNIT_FIELDS = new Set([
  "height",
  "weight",
  "chestCircumference",
]);

const CM_PER_INCH = 2.54;
const KG_PER_LB = 0.45359237;
const LBS_PER_STONE = 14;

const parseNumber = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundTo = (value, decimals = 1) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export const isMeasurementUnitField = (fieldId) =>
  MEASUREMENT_UNIT_FIELDS.has(fieldId);

export const getDefaultMeasurementSystem = (language = "") =>
  language.toLowerCase().startsWith("en-us") ? "imperial" : "metric";

export const measurementUnitLabels = {
  height: {
    metric: ["cm"],
    imperial: ["ft", "in"],
  },
  chestCircumference: {
    metric: ["cm"],
    imperial: ["in"],
  },
  weight: {
    metric: ["kg"],
    imperial: ["st", "lb"],
  },
};

export const toCanonicalMeasurement = (fieldId, unitSystem, parts) => {
  if (unitSystem === "metric") {
    const value = parseNumber(parts?.value);
    return value == null ? "" : roundTo(value, 1);
  }

  if (fieldId === "height") {
    const feet = parseNumber(parts?.feet) ?? 0;
    const inches = parseNumber(parts?.inches) ?? 0;
    if (parts?.feet === "" && parts?.inches === "") return "";
    return roundTo((feet * 12 + inches) * CM_PER_INCH, 1);
  }

  if (fieldId === "chestCircumference") {
    const inches = parseNumber(parts?.inches);
    return inches == null ? "" : roundTo(inches * CM_PER_INCH, 1);
  }

  if (fieldId === "weight") {
    const stones = parseNumber(parts?.stones) ?? 0;
    const pounds = parseNumber(parts?.pounds) ?? 0;
    if (parts?.stones === "" && parts?.pounds === "") return "";
    return roundTo((stones * LBS_PER_STONE + pounds) * KG_PER_LB, 1);
  }

  return "";
};

export const fromCanonicalMeasurement = (fieldId, unitSystem, value) => {
  const canonical = parseNumber(value);
  if (canonical == null) {
    if (unitSystem === "imperial" && fieldId === "height") {
      return { feet: "", inches: "" };
    }
    if (unitSystem === "imperial" && fieldId === "weight") {
      return { stones: "", pounds: "" };
    }
    if (unitSystem === "imperial") return { inches: "" };
    return { value: "" };
  }

  if (unitSystem === "metric") return { value: String(canonical) };

  if (fieldId === "height") {
    const totalInches = canonical / CM_PER_INCH;
    const feet = Math.floor(totalInches / 12);
    const inches = roundTo(totalInches - feet * 12, 1);
    return { feet: String(feet), inches: String(inches) };
  }

  if (fieldId === "chestCircumference") {
    return { inches: String(roundTo(canonical / CM_PER_INCH, 1)) };
  }

  if (fieldId === "weight") {
    const totalPounds = canonical / KG_PER_LB;
    const stones = Math.floor(totalPounds / LBS_PER_STONE);
    const pounds = roundTo(totalPounds - stones * LBS_PER_STONE, 1);
    return { stones: String(stones), pounds: String(pounds) };
  }

  return { value: String(canonical) };
};

export const normaliseMeasurementFormDataForSave = (formData = {}) => {
  const next = { ...formData };

  MEASUREMENT_UNIT_FIELDS.forEach((fieldId) => {
    if (!(fieldId in next)) return;
    const value = parseNumber(next[fieldId]);
    next[fieldId] = value == null ? next[fieldId] : roundTo(value, 1);
  });

  return next;
};
